import DeleteOutline from "@mui/icons-material/DeleteOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid-pro";

import { VARIABLE_DATA_TYPE_OPTIONS } from "@/constants/rule-variables";

import CoAppDataGridDateTimeCell from "@/components/global/components/datagrid/CoAppDataGridDateTimeCell";
import CoAppDataGridNameCell from "@/components/global/components/datagrid/CoAppDataGridNameCell";
import RowActionIcons from "@/components/global/components/RowActionIcons";

import type { Collection } from "../collectionsApi";
import messages from "@/constants/messages";

export const collectionColumns = [
	"name",
	"rulesUsingCount",
	"dataTypeLabel",
	"platformName",
	"createdAt",
	"updatedAt",
	"actions",
];

export function collectionDataTypeLabel(dataTypeId: number): string {
	return VARIABLE_DATA_TYPE_OPTIONS.find((o) => o.id === dataTypeId)?.label ?? String(dataTypeId);
}

export function getCollectionManagementColumns(
	onOpenEntries: (collectionId: string) => void,
	onDeleteCollection: (collection: Collection) => void,
): GridColDef<Collection>[] {
	return [
		{
			field: "name",
			headerName: "Name",
			flex: 0.5,
			sortable: true,
			renderCell: (params: GridRenderCellParams<Collection>) => (
				<CoAppDataGridNameCell item={params.row} />
			),
		},
		{
			field: "rulesUsingCount",
			headerName: "# of Rules Using this Collection",
			flex: 0.6,
			sortable: true,
			type: "number",
			align: "center",
		},
		{
			field: "entryCount",
			headerName: "# of Entries",
			headerAlign: "center",
			flex: 0.6,
			sortable: true,
			type: "number",
			align: "center",
		},
		{
			field: "dataTypeLabel",
			headerName: "Data Type",
			flex: 0.4,
			sortable: true,
			valueGetter: (_value, row) => collectionDataTypeLabel(row.dataTypeId),
		},
		{
			field: "platformName",
			headerName: "Platform",
			flex: 0.4,
			sortable: true,
			renderCell: (params: GridRenderCellParams<Collection>) => (
				<CoAppDataGridNameCell item={{ name: params.row.platformName }} />
			),
		},
		{
			field: "createdAt",
			headerName: "Created At",
			flex: 0.5,
			sortable: true,
			renderCell: (params: GridRenderCellParams<Collection, string>) => (
				<CoAppDataGridDateTimeCell value={params.value} />
			),
		},
		{
			field: "updatedAt",
			headerName: "Updated At",
			flex: 0.5,
			sortable: true,
			renderCell: (params: GridRenderCellParams<Collection, string>) => (
				<CoAppDataGridDateTimeCell value={params.value} />
			),
		},
		{
			field: "actions",
			headerName: "",
			type: "actions",
			sortable: false,
			width: 88,
			renderCell: (params: GridRenderCellParams<Collection>) => {
				const inUse = params.row.rulesUsingCount > 0;
				return (
					<RowActionIcons
						actions={[
							{
								key: "manage",
								label: "Manage collection entries",
								icon: <EditOutlined />,
								onClick: () => onOpenEntries(String(params.row.id)),
							},
							{
								key: "delete",
								label: "Delete",
								icon: <DeleteOutline />,
								onClick: () => onDeleteCollection(params.row),
								destructive: true,
								disabled: inUse,
								disabledLabel: messages.COLLECTION_IN_USE_DELETE_TOOLTIP,
							},
						]}
					/>
				);
			},
		},
	];
}
